import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

type Product = {
  name: string;
  icon: string;
  category: string;
  categoryColor: string;
  tagline: string;
  audience: string;
  href?: string;
  videoSrc?: string;
  anchorId?: string;
};

const products: Product[] = [
  {
    name: 'Will AI Replace My Job?',
    icon: '🤖',
    category: 'AI Tool · Consumer',
    categoryColor: 'amber',
    tagline:
      'Find your AI displacement risk score in 60 seconds. Answer a short questionnaire, get a free risk score — or upgrade for a full personalized digital report. 30+ roles covered.',
    audience: 'General public, career changers, HR professionals',
    href: 'https://aiadvisor.ssbsconsulting.com',
  },
  {
    name: 'Utsave',
    icon: '🎉',
    category: 'Event Tech · Consumer',
    categoryColor: 'coral',
    tagline:
      'Plan your celebration. Bring your people together. Invitations, RSVP tracking, photo sharing, WhatsApp reminders — designed for South Asian family celebrations and cultural milestones.',
    audience: 'South Asian families, cultural event organizers, community leaders',
    href: 'https://www.utsav-events.com',
  },
  {
    name: 'Turnify',
    icon: '↩️',
    category: 'B2B SaaS · AI-powered',
    categoryColor: 'teal',
    tagline:
      'AI-powered B2B returns management. Automated approval using AI risk analysis, fraud detection, SAP S/4 HANA integration, configurable workflows, and real-time insights for retail operations.',
    audience: 'Retail operations, supply chain managers, SAP ERP teams',
    href: 'https://b2b-returns.ssbsconsulting.com',
  },
  {
    name: 'SQL Practice Platform',
    icon: '🗃️',
    category: 'EdTech · Training',
    categoryColor: 'indigo',
    tagline:
      'Master SQL with hands-on exercises drawn from Banking, Retail, ERP, and Telecom data models. JOINs, window functions, aggregates, subqueries — with AI-powered hints and score tracking.',
    audience: 'Data analysts, developers, SQL students, training instructors',
    href: 'https://sqllab.ssbsconsulting.com',
  },
  {
    name: 'Automatic Invoice Processing',
    icon: '📄',
    category: 'Enterprise AI · Client Demo',
    categoryColor: 'sky',
    tagline:
      'Intelligent capture, validation, and workflow routing for invoices — recorded demo from a client engagement showcasing end-to-end AI automation.',
    audience: 'Finance, AP, shared services, and operations teams',
    videoSrc: '/videos/invoice-processing-demo.mp4',
    anchorId: 'product-invoice-processing',
  },
  {
    name: 'Little Sprouts Daycare Mobile App',
    icon: '🌱',
    category: 'Mobile App · Childcare',
    categoryColor: 'lime',
    tagline:
      'A mobile-first daycare operations app for check-in, daily schedules, payments, and family profiles — built for independent childcare providers.',
    audience: 'Home daycare owners, family childcare providers, classroom staff',
    href: '/little_sprouts_daycare_mobile_app.html',
  },
];

const services = [
  {
    icon: '🧠',
    name: 'AI Strategy & Implementation',
    desc: 'From use-case discovery to GenAI pilots and LLM integration. We help you find where AI creates real value — then build it.',
    tags: ['GenAI', 'LLMs', 'Strategy'],
    link: '/ai-services',
  },
  {
    icon: '📊',
    name: 'Data Engineering & Analytics',
    desc: 'Modern data platforms, pipelines, ERP analytics, and BI solutions. SQL to Spark, dbt to dashboards.',
    tags: ['Data Platforms', 'SQL', 'SAP'],
    link: '/data-services',
  },
  {
    icon: '⚡',
    name: 'Agile Transformation',
    desc: 'Coaching teams and leadership through SAFe, Scrum, and delivery acceleration. Practical, not theoretical.',
    tags: ['SAFe', 'Scrum', 'Coaching'],
    link: '/agile-services',
  },
  {
    icon: '💻',
    name: 'Custom Software Development',
    desc: 'Full-stack AI-powered application development. We prototype fast and scale thoughtfully — proven by our own products.',
    tags: ['Next.js', 'React', 'AI Apps'],
    link: '/contact',
  },
];

const whyItems = [
  {
    title: 'Product-proven thinking',
    desc: 'Every service offering is backed by products we have built and launched ourselves. We know what works in production.',
  },
  {
    title: 'AI-first, not AI-last',
    desc: "We don't bolt on AI as an afterthought. From Turnify's fraud detection to the SQL platform's hint engine, AI is core to how we build.",
  },
  {
    title: 'Enterprise + startup sensibility',
    desc: 'Deep experience with SAP S/4, ERP ecosystems, and enterprise delivery — combined with the speed and agility of a product studio.',
  },
  {
    title: 'End-to-end capability',
    desc: 'Strategy through execution. We engage at the problem definition stage and stay through production launch.',
  },
];

const techStack = [
  'Next.js', 'React', 'Python', 'PostgreSQL',
  'SAP S/4', 'OpenAI / Claude', 'Supabase', 'Vercel',
  'dbt', 'Tableau',
];

const testimonials = [
  {
    quote:
      'SSB Solutions brought a rare combination of strategic vision and hands-on delivery. They helped us cut our release cycle in half and stood up a GenAI prototype in under 6 weeks.',
    name: 'Director of Engineering',
    org: 'Mid-Market Financial Services Firm',
    initials: 'DE',
  },
  {
    quote:
      'The Agile transformation coaching was practical, direct, and stuck. No theory for theory\'s sake — they embedded with our teams and drove real change in how we plan and deliver.',
    name: 'VP of Product Delivery',
    org: 'Retail Operations Company',
    initials: 'VP',
  },
  {
    quote:
      'Their data engineering team rebuilt our SAP reporting pipeline in a fraction of the time we estimated internally. Accurate, fast, and built to scale.',
    name: 'Head of Analytics',
    org: 'Supply Chain & Logistics Enterprise',
    initials: 'HA',
  },
];

const trustBadges = [
  { label: 'SAFe SPC', sub: 'Program Consultant' },
  { label: 'SAFe SSM', sub: 'Scrum Master' },
  { label: 'AWS Certified', sub: 'Cloud Practitioner' },
  { label: 'ICAgile', sub: 'Certified Professional' },
  { label: 'IASA', sub: 'Architecture' },
  { label: 'SAFe POPM', sub: 'Product Owner / PM' },
];

const Home: React.FC = () => {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  useEffect(() => {
    if (!activeVideo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveVideo(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeVideo]);

  useEffect(() => {
    const id = window.location.hash.replace(/^#/, '');
    if (!id) return;
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  return (
    <main className="home-page">

      {/* ── HERO ── */}
      <section className="home-hero section-shell">
        <div className="hero-copy">
          <div className="hero-eyebrow">
            <span className="hero-eyebrow-dot" aria-hidden="true" />
            AI · Data · Agile · Products
          </div>
          <h1>We consult.<br /><em>We build.</em><br />We ship.</h1>
          <p className="hero-description">
            SSBS is a consulting practice and a product studio. We help enterprises navigate AI
            and data — and we build software products people actually use.
          </p>
          <div className="hero-actions">
            <a href="#products" className="btn btn-primary">Explore Products →</a>
            <a href="#services" className="btn btn-outline">View Services</a>
          </div>
          <div className="hero-stats">
            <div className="stat">
              <strong>10+</strong>
              <span>Years of expertise</span>
            </div>
            <div className="stat">
              <strong>6</strong>
              <span>Products &amp; demos</span>
            </div>
            <div className="stat">
              <strong>3</strong>
              <span>Industry verticals</span>
            </div>
          </div>
        </div>

        <div className="hero-product-grid">
          {products.map((product) => {
            const isVideo = Boolean(product.videoSrc);
            const isLink = Boolean(product.href) && !isVideo;
            const className = `hero-mini-card hero-mini-card--${product.categoryColor}${isVideo || isLink ? ' hero-mini-card--interactive' : ''}`;
            const content = (
              <>
                <span className="mini-icon">{product.icon}</span>
                <h3>{product.name}</h3>
                <p className={`mini-cat mini-cat--${product.categoryColor}`}>{product.category}</p>
                {isVideo ? (
                  <span className="hero-mini-demo-hint">▶ Click to play demo</span>
                ) : null}
              </>
            );

            if (isLink) {
              return (
                <a
                  className={className}
                  key={product.name}
                  href={product.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {content}
                </a>
              );
            }

            return (
              <article
                className={className}
                key={product.name}
                id={product.anchorId ? `${product.anchorId}-hero` : undefined}
                onClick={isVideo ? () => setActiveVideo(product.videoSrc as string) : undefined}
                onKeyDown={
                  isVideo
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setActiveVideo(product.videoSrc as string);
                        }
                      }
                    : undefined
                }
                role={isVideo ? 'button' : undefined}
                tabIndex={isVideo ? 0 : undefined}
              >
                {content}
              </article>
            );
          })}
        </div>
      </section>

      {/* ── PRODUCTS ── */}
      <div className="section-divider" />
      <section id="products" className="section-shell products-section">
        <div className="section-heading">
          <p className="eyebrow">Our Products</p>
          <h2>Built by us. Used by real people.</h2>
          <p className="section-sub">
            From AI-powered career tools to enterprise returns management — every product solves a
            real problem we believed in enough to ship. Includes a recorded client demo for
            intelligent invoice processing.
          </p>
        </div>
        <div className="products-grid">
          {products.map((product) => (
            <article className={`product-card product-card--${product.categoryColor}`} key={product.name} id={product.anchorId}>
              <div className="product-top">
                <span className={`product-icon-wrap product-icon-wrap--${product.categoryColor}`}>
                  {product.icon}
                </span>
                <span className={`pill pill--${product.categoryColor}`}>{product.category}</span>
              </div>
              <h3>{product.name}</h3>
              <p className="product-tagline">{product.tagline}</p>
              <small className="product-audience">🎯 {product.audience}</small>
              {product.videoSrc ? (
                <>
                  <button
                    type="button"
                    className={`product-video-launch product-video-launch--${product.categoryColor}`}
                    onClick={() => setActiveVideo(product.videoSrc as string)}
                    aria-label={`Play video demo: ${product.name}`}
                  >
                    <span className="product-video-launch__play" aria-hidden>▶</span>
                    <span>Watch demo video</span>
                  </button>
                  <p className="product-video-hint">Opens fullscreen player. Press Esc to close.</p>
                </>
              ) : (
                <a
                  href={product.href}
                  target="_blank"
                  rel="noreferrer"
                  className={`product-link product-link--${product.categoryColor}`}
                >
                  Launch App →
                </a>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* ── SERVICES ── */}
      <div className="section-divider" />
      <section id="services" className="section-shell services-section">
        <div className="section-heading">
          <p className="eyebrow">Consulting Services</p>
          <h2>Where deep expertise meets execution.</h2>
          <p className="section-sub">
            We don't just advise — we build alongside you. Our consulting is shaped by years of
            shipping real products in complex enterprise environments.
          </p>
        </div>
        <div className="services-grid">
          {services.map((svc) => (
            <article className="service-card" key={svc.name}>
              <div className="service-icon">{svc.icon}</div>
              <h3>{svc.name}</h3>
              <p>{svc.desc}</p>
              <div className="service-tags">
                {svc.tags.map((tag) => (
                  <span className="service-tag" key={tag}>{tag}</span>
                ))}
              </div>
              <Link to={svc.link} className="service-learn-more">Learn more →</Link>
            </article>
          ))}
        </div>
      </section>

      {/* ── WHY SSBS ── */}
      <div className="section-divider" />
      <section className="section-shell why-section">
        <div className="why-grid">
          <div className="why-left">
            <p className="eyebrow">Why SSBS</p>
            <h2>Consultants who<br />actually ship.</h2>
            <div className="why-list">
              {whyItems.map((item) => (
                <div className="why-item" key={item.title}>
                  <span className="why-dot" aria-hidden="true" />
                  <div>
                    <div className="why-item-title">{item.title}</div>
                    <div className="why-item-desc">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="why-panel">
            <div className="why-panel-title">Our technology stack</div>
            <div className="why-panel-sub">What we build with — and advise on</div>
            <div className="tech-grid">
              {techStack.map((tech) => (
                <div className="tech-item" key={tech}>{tech}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST BADGES ── */}
      <div className="section-divider" />
      <section className="section-shell trust-section">
        <p className="eyebrow" style={{ textAlign: 'center' }}>Credentials &amp; Certifications</p>
        <div className="trust-badges-grid">
          {trustBadges.map((b) => (
            <div className="trust-badge" key={b.label}>
              <div className="trust-badge-label">{b.label}</div>
              <div className="trust-badge-sub">{b.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <div className="section-divider" />
      <section className="section-shell testimonials-section">
        <div className="section-heading">
          <p className="eyebrow">Client Feedback</p>
          <h2>Trusted by teams who ship.</h2>
          <p className="section-sub">
            A sample of feedback from engagements across financial services, retail, and enterprise
            technology. Client names kept confidential on request.
          </p>
        </div>
        <div className="testimonials-grid">
          {testimonials.map((t) => (
            <article className="testimonial-card" key={t.name}>
              <div className="testimonial-quote-mark">"</div>
              <p className="testimonial-quote">{t.quote}</p>
              <div className="testimonial-author">
                <div className="testimonial-avatar">{t.initials}</div>
                <div>
                  <div className="testimonial-name">{t.name}</div>
                  <div className="testimonial-org">{t.org}</div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ── LEAD MAGNET ── */}
      <div className="section-divider" />
      <section className="section-shell lead-magnet-section">
        <div className="lead-magnet-inner">
          <div className="lead-magnet-copy">
            <p className="eyebrow">Free Resource</p>
            <h2>Is your organization<br />AI-ready?</h2>
            <p>
              Download our free <strong>AI Readiness Guide</strong> — a practical 8-point
              framework for executives and delivery leaders evaluating AI adoption. No fluff, no
              vendor pitch. Just the questions you need to answer before you invest.
            </p>
            <ul className="lead-magnet-bullets">
              <li>How to assess your data foundation for AI</li>
              <li>5 common AI pitfalls enterprises fall into</li>
              <li>The 3-stage AI adoption roadmap</li>
              <li>Build vs. buy decision framework</li>
            </ul>
          </div>
          <div className="lead-magnet-form-wrap">
            <div className="lead-magnet-card">
              <div className="lead-magnet-card-title">Get the Free Guide</div>
              <div className="lead-magnet-card-sub">Delivered instantly to your inbox.</div>
              <form
                className="lead-magnet-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.target as HTMLFormElement;
                  const email = (form.elements.namedItem('lm-email') as HTMLInputElement)?.value;
                  if (email) {
                    alert(`Thanks! We'll send the AI Readiness Guide to ${email} shortly.`);
                    form.reset();
                  }
                }}
              >
                <input
                  type="text"
                  name="lm-name"
                  placeholder="Your name"
                  className="lm-input"
                  required
                />
                <input
                  type="email"
                  name="lm-email"
                  placeholder="Work email"
                  className="lm-input"
                  required
                />
                <button type="submit" className="lm-btn">
                  Send Me the Guide →
                </button>
              </form>
              <p className="lead-magnet-disclaimer">
                No spam. Unsubscribe any time. We respect your privacy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="section-shell">
        <div className="cta-banner">
          <p className="eyebrow" style={{ display: 'block', marginBottom: '1rem' }}>Get in Touch</p>
          <h2>Ready to build something<br />that actually ships?</h2>
          <p>
            Whether you need consulting expertise or want to explore our products — let's start a
            conversation.
          </p>
          <div className="cta-btns">
            <Link to="/contact" className="btn btn-primary">Schedule a Call →</Link>
            <a href="#products" className="btn btn-outline">Explore Products</a>
          </div>
        </div>
      </div>

      {/* ── VIDEO MODAL ── */}
      {activeVideo ? (
        <div
          className="video-modal-backdrop"
          role="presentation"
          onClick={() => setActiveVideo(null)}
        >
          <div
            className="video-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Product demo video"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="video-modal-close"
              onClick={() => setActiveVideo(null)}
              aria-label="Close video"
            >
              ×
            </button>
            <video
              className="video-modal-player"
              src={activeVideo}
              controls
              autoPlay
              playsInline
            />
          </div>
        </div>
      ) : null}
    </main>
  );
};

export default Home;
